// Carrier tracking timeline helpers — shared by the old order page
// (OrderTables.vue, detail modal) and the new one (OrdersNew.vue, tracking
// modal). Spec: the 2026-10-07 BO orders status-tabs design, §20 R48.
//
// Payload = GET /api/v1/orders/:id/tracking-timeline (mdf-bo-api
// controllers/BO-OrderTrackingTimeline.go) → resp.data.Datum:
//   { bo_order_id, tracking_number, shipping_status_name, shipping_status_date,
//     timeline: [{ ts, source, status_code, status, message?, detail? }],
//     last_event_at, last_updated_at }
// `ts` = unix SECONDS; `last_updated_at` = RFC3339 string (or null).
// `detail` = the raw carrier payload (Thaipost / SPX: status_detail, location,
// postcode, office_name, office_tel, ...). The writer stores it; whether the
// read endpoint forwards it depends on the BE version, so every helper falls
// back without it.

function isEvent(ev) {
    return !!ev && typeof ev === "object" && !Array.isArray(ev);
}

function detailOf(ev) {
    return isEvent(ev) && ev.detail && typeof ev.detail === "object" ? ev.detail : null;
}

/** Status text: carrier `detail.status_detail`, else the event `status`, else "". */
export function timelineStatusLine(ev) {
    const d = detailOf(ev);
    if (d && d.status_detail) {
        return d.status_detail;
    }
    return ev && ev.status ? ev.status : "";
}

/** "<location> <postcode>" from the carrier detail ("" when neither). */
export function timelineLocation(ev) {
    const d = detailOf(ev);
    if (!d) return "";
    const parts = [];
    if (d.location) parts.push(d.location);
    if (d.postcode) parts.push(d.postcode);
    return parts.join(" ");
}

export function timelineOffice(ev) {
    const d = detailOf(ev);
    return d && d.office_name ? d.office_name : "";
}

export function timelineOfficeTel(ev) {
    const d = detailOf(ev);
    return d && d.office_tel ? d.office_tel : "";
}

/**
 * "YYYY-MM-DD HH:mm" (browser local time). Numbers are unix seconds (event
 * `ts`); strings go through Date (last_updated_at). Empty → ""; unparsable →
 * the value as text.
 */
export function formatTimelineTs(value) {
    if (!value) return "";
    let d;
    if (typeof value === "number") {
        d = new Date(value * 1000);
    } else {
        d = new Date(value);
    }
    if (isNaN(d.getTime())) return String(value);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function tsOf(ev) {
    const n = Number(ev.ts);
    return isFinite(n) ? n : 0;
}

/**
 * Oldest → newest by `ts` (missing ts = 0, first). Stable: equal ts keep the
 * carrier's order. Non-array → []; non-object entries are dropped. Never
 * mutates the input. (Old page: newest at the bottom.)
 */
export function sortTimelineAsc(timeline) {
    if (!Array.isArray(timeline)) return [];
    return timeline.filter(isEvent).sort((a, b) => tsOf(a) - tsOf(b));
}

/**
 * Newest → oldest (§20 R48: latest on top). The exact reverse of
 * sortTimelineAsc, so on equal ts the event the carrier listed last wins.
 */
export function sortTimelineDesc(timeline) {
    return sortTimelineAsc(timeline).reverse();
}

/** The events of a response Datum (any shape) — [] when there are none. */
export function timelineEvents(datum) {
    return datum && typeof datum === "object" && Array.isArray(datum.timeline) ? datum.timeline : [];
}

/**
 * §20 R48 — rows of the OrdersNew tracking modal, newest first:
 *   { key, ts, time, status, statusCode, source, location, office, officeTel, message, latest }
 * `latest` is true on the first row only (the modal highlights it).
 */
export function timelineRows(datum) {
    return sortTimelineDesc(timelineEvents(datum)).map((ev, i) => ({
        key: `${tsOf(ev)}-${i}`,
        ts: tsOf(ev),
        time: formatTimelineTs(ev.ts),
        status: timelineStatusLine(ev),
        statusCode: ev.status_code ? String(ev.status_code) : "",
        source: ev.source ? String(ev.source) : "",
        location: timelineLocation(ev),
        office: timelineOffice(ev),
        officeTel: timelineOfficeTel(ev),
        message: ev.message ? String(ev.message) : "",
        latest: i === 0
    }));
}
